import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useGetAgenticRunDetailQuery,
  useGetFanOutArtifactsQuery,
} from "../../../app/api/console/agenticRunsApiSlice";
import { OutcomeChips } from "./index";
import {
  RUN_STATUS_CONFIG,
  formatUsd,
  formatDuration,
  timeAgo,
  displayRunStatus,
} from "./outcomeConfig";
import { OutcomeBadge } from "./OutcomeBadge";
import EventsTimeline from "./EventsTimeline";
import FanOutArtifacts from "./FanOutArtifacts";
import RunReconciliation from "./RunReconciliation";
import { hubScanUrl } from "./hubScanLinks";
import TraceExportButton from "./TraceExportButton";

// Outcome classes that need an operator's eyes. Everything else (the skill ran
// and reported, with or without findings) is a normal result.
const CLEAN_OUTCOMES = new Set(["ok_findings", "ok_no_findings"]);

function isAnomaly(worker) {
  return !CLEAN_OUTCOMES.has(worker.outcome_class) || Boolean(worker.error);
}

function MetaItem({ label, children }) {
  return (
    <div>
      <div className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
        {label}
      </div>
      <div className='text-sm text-gray-800 mt-0.5 break-all'>{children}</div>
    </div>
  );
}

function WorkerRow({
  worker,
  env,
  orchestratorRunId,
  identity,
  showSkill,
  originPrefix,
}) {
  const [expanded, setExpanded] = useState(false);
  // Every worker of a fan-out usually hits the same host; repeating it 200
  // times pushes the discriminating part of the path out of view.
  const urlLabel =
    originPrefix && worker.url?.startsWith(originPrefix)
      ? worker.url.slice(originPrefix.length) || "/"
      : worker.url || "—";
  const workerRun = worker.run_id
    ? {
        run_id: worker.run_id,
        agent_id: worker.skill,
        skill_label: worker.skill,
      }
    : null;
  const viewport = identity?.viewport;

  return (
    <>
      <tr
        onClick={() => setExpanded((v) => !v)}
        className='border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer'>
        {/* One line per worker. The aggregate stores only `url`, so a viewport
            matrix renders as N identical rows; test_id / viewport come from
            the artifacts endpoint's items.json ↔ results join and sit inline
            rather than on a second line — at 200 URLs the extra line doubles
            the table's height for information that fits beside the url. */}
        <td className='px-4 py-1 max-w-0 w-full'>
          <div className='flex items-baseline gap-2 min-w-0'>
            <span className='truncate text-gray-800 text-xs' title={worker.url}>
              {urlLabel}
            </span>
            {identity?.test_id && (
              <span className='text-[10px] font-mono text-indigo-700 bg-indigo-50 px-1 rounded shrink-0'>
                {identity.test_id}
              </span>
            )}
            {viewport?.width && (
              <span className='text-[10px] text-gray-400 font-mono shrink-0'>
                {viewport.width}×{viewport.height}
              </span>
            )}
          </div>
        </td>
        {showSkill && (
          <td className='px-4 py-1 text-gray-600 text-xs whitespace-nowrap'>
            {worker.skill}
          </td>
        )}
        <td className='px-4 py-1'>
          <OutcomeBadge cls={worker.outcome_class} />
        </td>
        <td className='px-4 py-1 text-gray-600 text-xs whitespace-nowrap tabular-nums'>
          {formatDuration((worker.duration_ms || 0) / 1000)}
        </td>
        <td className='px-4 py-1 text-gray-600 text-xs tabular-nums'>
          {formatUsd(worker.cost_usd)}
        </td>
        <td className='px-4 py-1 text-gray-500 text-xs tabular-nums'>
          {worker.iterations ?? "—"}
        </td>
      </tr>
      {expanded && (
        <tr className='bg-gray-50/70 border-b border-gray-100'>
          <td colSpan={showSkill ? 6 : 5} className='px-4 py-3 space-y-3' onClick={(e) => e.stopPropagation()}>
            {workerRun ? (
              <EventsTimeline
                env={env}
                runId={orchestratorRunId || worker.run_id}
                fanOuts={[]}
                orchestratorRun={workerRun}
                focusRunId={worker.run_id}
                autoLoad
                compact
              />
            ) : (
              <div className='text-xs text-gray-500'>No worker run_id — trace unavailable.</div>
            )}
            {(worker.extra_context || worker.error || worker.final_text) && (
              <details className='rounded-lg border border-gray-200 bg-white'>
                <summary className='cursor-pointer px-3 py-2 text-xs font-medium text-gray-600'>
                  Worker summary (final text, errors, context)
                </summary>
                <div className='px-3 pb-3 space-y-2 border-t border-gray-100'>
                  {worker.extra_context && (
                    <div>
                      <span className='text-[11px] font-semibold text-gray-500 uppercase'>
                        Extra context
                      </span>
                      <pre className='text-xs text-gray-600 whitespace-pre-wrap mt-1'>
                        {worker.extra_context}
                      </pre>
                    </div>
                  )}
                  {worker.error && (
                    <div>
                      <span className='text-[11px] font-semibold text-red-500 uppercase'>
                        Error
                      </span>
                      <pre className='text-xs text-red-700 whitespace-pre-wrap mt-1'>
                        {worker.error}
                      </pre>
                    </div>
                  )}
                  {worker.final_text && (
                    <div>
                      <span className='text-[11px] font-semibold text-gray-500 uppercase'>
                        Final text{worker.final_text_truncated ? " (truncated)" : ""}
                      </span>
                      <pre className='text-xs text-gray-700 whitespace-pre-wrap mt-1 max-h-[240px] overflow-y-auto'>
                        {worker.final_text}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function FanOutCard({ fanOut, env, orchestratorRunId }) {
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  // Workers table open with the full list by default: it is the substance of a
  // fan-out, and the anomaly count in the header already says whether any of it
  // needs attention. Unticking the box narrows to the workers to check.
  const [workersOpen, setWorkersOpen] = useState(true);
  const [showClean, setShowClean] = useState(true);

  const workers = fanOut.workers || [];
  const anomalies = workers.filter(isAnomaly);
  const returned = workers.length;
  const dispatched = Number(fanOut.total_workers) || 0;
  const missingReturns = dispatched > 0 && returned < dispatched;

  // Worker identities ride along with the artifacts payload, so they are only
  // available once that panel has been opened at least once — RTK Query keeps
  // it cached, and the labels then persist for the workers table.
  const { data: artifacts } = useGetFanOutArtifactsQuery(
    { env, runId: orchestratorRunId, fanOutId: fanOut.fan_out_id },
    { skip: !artifactsOpen && !workersOpen }
  );
  const identities = artifacts?.worker_identities || {};

  // Anomalies first when collapsed-by-default would otherwise bury them.
  const visibleWorkers = showClean ? workers : anomalies;

  // Anything identical across every worker is chrome, not data: hoist it to
  // the table caption so each row carries only what distinguishes it.
  const skills = new Set(workers.map((w) => w.skill).filter(Boolean));
  const commonSkill = skills.size === 1 ? [...skills][0] : null;
  const origins = new Set(
    workers
      .map((w) => {
        try {
          return new URL(w.url).origin;
        } catch {
          return null;
        }
      })
      .filter(Boolean)
  );
  const commonOrigin = origins.size === 1 ? [...origins][0] : null;

  return (
    <div className='bg-white border border-gray-200 rounded-xl overflow-hidden'>
      <div className='px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2'>
        <div className='flex items-center gap-3'>
          <span className='font-medium text-sm text-gray-800'>{fanOut.fan_out_id}</span>
          <span className='text-xs text-gray-400' title={fanOut.started_at}>
            {timeAgo(fanOut.started_at)}
          </span>
          {fanOut.matched_by === "time_window" && (
            <span
              className='text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500'
              title='Legacy fan-out without parent_run_id provenance — matched by time window'>
              time-matched
            </span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          <OutcomeChips counts={fanOut.outcome_counts} max={6} />
          <span className='text-xs text-gray-500'>
            {fanOut.succeeded}/{fanOut.total_workers} ok · {formatUsd(fanOut.total_cost_usd)}
          </span>
        </div>
      </div>

      {/* Per-fan-out link of the chain. The scope side is run-level (see
          RunReconciliation) and the precise per-fan-out scope↔items check
          lives in the artifacts panel, one S3 read deeper. */}
      <div className='px-4 py-2 flex flex-wrap items-center gap-3 text-xs border-b border-gray-100'>
        <span className='text-gray-500'>
          dispatch{" "}
          <span className='font-medium text-gray-800 tabular-nums'>{dispatched}</span>
        </span>
        <span className='text-gray-300'>→</span>
        <span className='text-gray-500'>
          returned{" "}
          <span
            className={`font-medium tabular-nums ${
              missingReturns ? "text-amber-700" : "text-gray-800"
            }`}>
            {returned}
          </span>
        </span>
        {missingReturns && (
          <span className='px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-medium'>
            {dispatched - returned} missing
          </span>
        )}

        <button
          type='button'
          onClick={() => setArtifactsOpen((v) => !v)}
          className='ml-auto text-indigo-600 hover:text-indigo-800 font-medium'>
          {artifactsOpen ? "▾" : "▸"} artifacts
        </button>
      </div>

      {artifactsOpen && (
        <FanOutArtifacts
          env={env}
          runId={orchestratorRunId}
          fanOutId={fanOut.fan_out_id}
        />
      )}

      {/* Collapsed by default: a viewport-matrix fan-out is 200+ rows, and an
          always-open table is what makes this page unreadable at that size. */}
      <div className='px-4 py-2 flex flex-wrap items-center gap-3 text-xs border-t border-gray-100'>
        <button
          type='button'
          onClick={() => setWorkersOpen((v) => !v)}
          className='text-indigo-600 hover:text-indigo-800 font-medium'>
          {workersOpen ? "▾" : "▸"} workers ({workers.length})
        </button>
        {anomalies.length > 0 ? (
          <span className='px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-medium'>
            {anomalies.length} to check
          </span>
        ) : (
          <span className='text-gray-400'>nothing to check</span>
        )}
        {workersOpen && (
          <label className='flex items-center gap-1.5 text-gray-600 cursor-pointer select-none ml-auto'>
            <input
              type='checkbox'
              checked={showClean}
              onChange={(e) => setShowClean(e.target.checked)}
              className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            show the {workers.length - anomalies.length} clean workers
          </label>
        )}
      </div>

      {/* Past a dozen workers the table would push the events timeline off the
          page — a 200-url fan-out is several thousand pixels. Cap it and let it
          scroll on its own, with the header pinned. */}
      {workersOpen && (
        <div
          className={
            visibleWorkers.length > 12
              ? "max-h-[420px] overflow-y-auto border-t border-gray-100"
              : ""
          }>
        <table className='w-full text-sm'>
          {(commonOrigin || commonSkill) && (
            <caption className='caption-top px-4 py-1 text-left text-[11px] text-gray-400 bg-gray-50/60 border-b border-gray-100'>
              {commonOrigin && (
                <span className='font-mono'>
                  all urls on {commonOrigin}
                </span>
              )}
              {commonOrigin && commonSkill && " · "}
              {commonSkill && <span className='font-mono'>{commonSkill}</span>}
            </caption>
          )}
          <thead className='sticky top-0 z-10'>
            <tr className='bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200'>
              <th className='px-4 py-1.5 font-semibold'>
                {commonOrigin ? "Path" : "URL"}
              </th>
              {!commonSkill && <th className='px-4 py-1.5 font-semibold'>Skill</th>}
              <th className='px-4 py-1.5 font-semibold'>Outcome</th>
              <th className='px-4 py-1.5 font-semibold'>Duration</th>
              <th className='px-4 py-1.5 font-semibold'>Cost</th>
              <th className='px-4 py-1.5 font-semibold'>Iter.</th>
            </tr>
          </thead>
          <tbody>
            {visibleWorkers.length === 0 ? (
              <tr>
                <td
                  colSpan={commonSkill ? 5 : 6}
                  className='px-4 py-4 text-center text-xs text-gray-400'>
                  No worker needs attention — tick the box above to see all{" "}
                  {workers.length}.
                </td>
              </tr>
            ) : (
              visibleWorkers.map((w, i) => (
                <WorkerRow
                  key={`${w.run_id || i}`}
                  worker={w}
                  env={env}
                  orchestratorRunId={orchestratorRunId}
                  identity={identities[w.run_id]}
                  showSkill={!commonSkill}
                  originPrefix={commonOrigin}
                />
              ))
            )}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function RunDetail() {
  const { env, runId } = useParams();
  const { data, isLoading, error, refetch, isFetching } = useGetAgenticRunDetailQuery(
    { env, runId },
    // Live drill-down: refresh while the run is in flight.
    { pollingInterval: 15000 }
  );

  const run = data?.run;
  const displayStatus = displayRunStatus(run);
  const statusCfg = RUN_STATUS_CONFIG[displayStatus] || {};

  return (
    <div className='w-full max-w-none'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-3'>
          <Link
            to='/admin/control-tower'
            className='text-sm text-indigo-600 hover:text-indigo-800 font-medium'>
            ← Control Tower
          </Link>
          <span className='text-gray-300'>/</span>
          <span className='text-sm text-gray-500'>{runId}</span>
        </div>
        <div className='flex items-center gap-2'>
          <TraceExportButton env={env} runId={runId} />
          <button
            onClick={refetch}
            className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors'>
            <svg
              className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {isLoading && <div className='py-12 text-center text-gray-400'>Loading run…</div>}
      {error && (
        <div className='px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700'>
          {error?.status === 404
            ? "Run not found."
            : `Failed to load run: ${error?.data?.detail || error?.error || "unknown"}`}
        </div>
      )}

      {run && (
        <>
          {/* Run header */}
          <div className='bg-white border border-gray-200 rounded-xl p-5 mb-5'>
            <div className='flex flex-wrap items-center gap-3 mb-4'>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${
                  statusCfg.bg || "bg-gray-100"
                } ${statusCfg.text || "text-gray-600"}`}>
                {statusCfg.pulse && (
                  <span className='w-2 h-2 rounded-full bg-blue-500 animate-pulse' />
                )}
                {statusCfg.label || displayStatus || run.status}
              </span>
              {run.stopped ? (
                <span className='text-xs text-amber-700'>
                  Operator stopped on Hub (session killed)
                  {run.status && run.status !== "stopped"
                    ? ` · run row: ${run.status}`
                    : ""}
                </span>
              ) : null}
              <h1 className='text-lg font-semibold text-gray-900'>
                {run.skill_label || run.agent_id} ·{" "}
                {run.website_name || run.website_id}
              </h1>
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4'>
              <MetaItem label='Launched by'>{run.caller_id || "—"}</MetaItem>
              <MetaItem label='Started'>
                <span title={run.started_at}>{timeAgo(run.started_at)}</span>
              </MetaItem>
              <MetaItem label='Duration'>{formatDuration(run.duration_seconds)}</MetaItem>
              <MetaItem label='Cost'>
                {formatUsd(run.spent_usd)}
                {run.budget_max_usd > 0 && (
                  <span className='text-gray-400'> / {formatUsd(run.budget_max_usd)}</span>
                )}
              </MetaItem>
              <MetaItem label='Runtime'>{run.runtime || "—"}</MetaItem>
              <MetaItem label='Session'>
                <span className='text-xs'>{run.session_key}</span>
              </MetaItem>
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
                Worker outcomes
              </span>
              <OutcomeChips counts={run.outcome_counts} max={10} />
              {((data.published_issue_count ?? 0) > 0 ||
                (data.scan_ids || []).length > 0) && (
                <span
                  title='Real published issues for this run: visible_issues from scan-metadata when a scan exists, else the distinct ids the workers reported in issues_filed. The green chip counts workers, not issues.'
                  className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200'>
                  {data.published_issue_count ?? 0} issues published
                  {data.findings_total > (data.published_issue_count ?? 0) && (
                    <span className='text-emerald-500'>
                      {" "}/ {data.findings_total} total
                    </span>
                  )}
                </span>
              )}
            </div>

            {run.error && (
              <div className='mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-100'>
                <span className='text-[11px] font-semibold text-red-500 uppercase'>
                  Run error
                </span>
                <pre className='text-xs text-red-700 whitespace-pre-wrap mt-1'>
                  {run.error}
                </pre>
              </div>
            )}

            {(data.scan_ids || []).length > 0 && (
              <div className='mt-3 flex flex-wrap items-center gap-1.5'>
                <span className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
                  Scans
                </span>
                {data.scan_ids.map((sid) => {
                  const href = hubScanUrl(env, sid);
                  return (
                    <span
                      key={sid}
                      className='inline-flex items-center gap-0.5 rounded-md bg-gray-100 hover:bg-indigo-50 border border-transparent hover:border-indigo-200'>
                      <a
                        href={href}
                        target='_blank'
                        rel='noopener noreferrer'
                        title={`Open on Hub: ${href}`}
                        className='text-[11px] px-1.5 py-0.5 font-mono text-indigo-700 hover:text-indigo-900'>
                        {sid}
                        <span className='ml-1 text-[10px] text-indigo-400' aria-hidden>
                          ↗
                        </span>
                      </a>
                      <button
                        type='button'
                        onClick={() => navigator.clipboard?.writeText(sid)}
                        title='Copy scan id'
                        className='text-[10px] px-1.5 py-0.5 text-gray-400 hover:text-gray-700 border-l border-gray-200'>
                        copy
                      </button>
                    </span>
                  );
                })}
                {data.scan_ids.length === 1 && (
                  <a
                    href={hubScanUrl(env, data.scan_ids[0])}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline ml-1'>
                    Open scan on Hub ↗
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Fan-outs */}
          <div className='space-y-4 mb-5'>
            <h2 className='text-sm font-semibold text-gray-700'>
              Fan-outs ({(data.fan_outs || []).length})
            </h2>
            <RunReconciliation
              scopeRequested={data.scope_requested}
              fanOuts={data.fan_outs}
            />
            {(data.fan_outs || []).length === 0 ? (
              <div className='px-4 py-6 bg-white border border-gray-200 rounded-xl text-center text-sm text-gray-400'>
                No fan-outs matched to this run (yet). Workers appear here once
                the first fan-out aggregate is written.
              </div>
            ) : (
              data.fan_outs.map((fo) => (
                <FanOutCard key={fo.fan_out_id} fanOut={fo} env={env} orchestratorRunId={runId} />
              ))
            )}
          </div>

          {/* Events */}
          <div className='space-y-2'>
            <h2 className='text-sm font-semibold text-gray-700'>Events timeline</h2>
            <EventsTimeline
              env={env}
              runId={runId}
              fanOuts={data.fan_outs}
              orchestratorRun={run}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default RunDetail;
